import apiClient from '@/lib/apiClient';
import { type ApiSubItem } from '@/lib/sales-metrics';
import { ApiResponse } from '@/types';

export interface CatalogProductRow {
  code: string;
  name: string;
  price: number;
  isAumBased: boolean;
  rateCard: string | null;
  noDiscount: boolean;
  /** Rank in the AUM upgrade ladder (higher is better); null = not in it. */
  tier: number | null;
  /**
   * Catalogue default for the years-2+ multi-year discount (ADKJ-123), null =
   * follow the company-wide setting. Meaningless for AUM products (slab-priced
   * renewal, no discount concept) and single-term products.
   */
  multiYearDiscountPercent: number | null;
}

export interface AumBandTemplateRow {
  band: string;
  aumLowerCrores: number;
  aumUpperCrores: number;
}

export interface ProductSettings {
  multiYearRenewalDiscountPercent: number;
  fixedProducts: CatalogProductRow[];
  aumProducts: CatalogProductRow[];
  aumBandTemplate: AumBandTemplateRow[];
  /** Admin-configured sub-item catalogue per API bundle product. */
  apiSubItems: Record<string, ApiSubItem[]>;
  /** Admin-configured price options per website tier (CUSTOM = empty = free-text). */
  websitePriceSlabs: Record<string, number[]>;
}

export interface AumSlabPriceInput {
  aumLowerCrores: number;
  aumUpperCrores: number;
  setupPrice: number;
  renewalPrice: number;
}

export interface CreateProductPayload {
  code: string;
  name: string;
  isAumBased: boolean;
  noDiscount?: boolean;
  tier?: number | null;
  price?: number;
  slabs?: AumSlabPriceInput[];
  /** Catalogue default for years-2+ discount (ADKJ-123); omitted/null = follow setting. */
  multiYearDiscountPercent?: number | null;
}

export interface UpdateProductSettingsPayload {
  multiYearRenewalDiscountPercent?: number;
  products?: Array<{
    code: string;
    price?: number;
    tier?: number | null;
    /** Catalogue default for years-2+ discount (ADKJ-123). null clears it. */
    multiYearDiscountPercent?: number | null;
  }>;
  apiSubItems?: Record<string, ApiSubItem[]>;
  websitePriceSlabs?: Record<string, number[]>;
}

/** @deprecated Use CatalogProductRow */
export type FixedProductPrice = CatalogProductRow;

/** @deprecated Use ProductSettings */
export type PriceSettings = ProductSettings;

/** @deprecated Use UpdateProductSettingsPayload */
export type UpdatePriceSettingsPayload = UpdateProductSettingsPayload;

export const priceSettingsService = {
  getAll: async (): Promise<ProductSettings> => {
    const { data } = await apiClient.get<ApiResponse<ProductSettings>>('/admin/price-settings');
    if (!data.data) throw new Error('Product settings returned no data');
    return data.data;
  },

  createProduct: async (payload: CreateProductPayload): Promise<ProductSettings> => {
    const { data } = await apiClient.post<ApiResponse<ProductSettings>>(
      '/admin/price-settings/products',
      payload,
    );
    if (!data.data) throw new Error('Product create returned no data');
    return data.data;
  },

  deleteProduct: async (code: string): Promise<ProductSettings> => {
    const { data } = await apiClient.delete<ApiResponse<ProductSettings>>(
      `/admin/price-settings/products/${encodeURIComponent(code)}`,
    );
    if (!data.data) throw new Error('Product delete returned no data');
    return data.data;
  },

  update: async (payload: UpdateProductSettingsPayload): Promise<ProductSettings> => {
    const { data } = await apiClient.put<ApiResponse<ProductSettings>>(
      '/admin/price-settings',
      payload,
    );
    if (!data.data) throw new Error('Product settings update returned no data');
    return data.data;
  },
};
